import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";
import TransactionStatus from "@homegame/common/dist/enum/transaction-status.enum";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import Button from "@mui/material/Button";
import DoneIcon from "@mui/icons-material/Done";
import ClearIcon from "@mui/icons-material/Clear";
import ConfirmDialog from "../../core/components/confirmation-dialog.component";
import { useState } from "react";
import formatCurrency from "../../core/helpers/format-currency.helper";
import TransactionService from "../../core/service/transaction.service";
import { useToast } from "../../core/hooks/use-toast.hook";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

const useStyles = makeStyles(() =>
  createStyles({
    btnsContainer: {
      display: "flex",
      justifyContent: "center",
      width: "100%",
      paddingTop: 5,
      paddingLeft: 10,
      paddingRight: 10,
      "& button": {
        textShadow: "none",
        minWidth: 80,
        marginLeft: 15,
      },
    },
  }),
);

export default function TransactionButtons({
  transaction,
  onActionDone,
  userId,
}: {
  transaction: TransactionInterface;
  onActionDone: () => any;
  userId?: number;
}) {
  const classes = useStyles({});
  const transactionsService = new TransactionService();
  const { showToast } = useToast();

  const [transactionDialogState, setTransactionDialogState] = useState<{
    id: number | undefined;
    open: boolean;
    type: TransactionType;
    actionType: "approve" | "decline";
    confirmationMessage: string | undefined;
  }>({
    open: false,
    type: TransactionType.CASH_OUT,
    actionType: "approve",
    id: undefined,
    confirmationMessage: undefined,
  });

  const [isActionPending, setIsActionPending] = useState<boolean>(false);

  const onDialogClose = () => {
    setTransactionDialogState({
      id: undefined,
      confirmationMessage: undefined,
      open: false,
      type: TransactionType.CASH_OUT,
      actionType: "approve",
    });
  };

  const doAction = async () => {
    if (isActionPending) return;
    try {
      setIsActionPending(true);
      if (transactionDialogState.actionType === "approve") {
        await transactionsService.approve(transactionDialogState!.id as number);
      } else {
        await transactionsService.decline(transactionDialogState!.id as number);
      }
    } catch (e) {
      showToast((e as Error).message, "error");
    } finally {
      setIsActionPending(false);
      onDialogClose();
      if (onActionDone) onActionDone();
    }
  };

  const handleDialogOpen = (
    transaction: TransactionInterface,
    actionType: "approve" | "decline",
  ) => {
    setTransactionDialogState({
      id: transaction.id,
      open: true,
      type: transaction.type,
      actionType,
      confirmationMessage: `Are you sure you want to ${actionType} a ${transaction.type} of ${transaction.chips} chips (${formatCurrency(transaction.amount!, transaction.currency!)})?`,
    });
  };

  if (transaction.status !== TransactionStatus.PENDING) return <></>;
  if (
    transaction.type === TransactionType.DEBT &&
    userId === transaction.fromUserId
  )
    return <></>;

  return (
    <div className={classes.btnsContainer}>
      <Button
        variant="outlined"
        startIcon={<DoneIcon />}
        color="success"
        onClick={() => handleDialogOpen(transaction, "approve")}
      >
        Accept
      </Button>
      <Button
        variant="outlined"
        startIcon={<ClearIcon />}
        color="error"
        onClick={() => handleDialogOpen(transaction, "decline")}
      >
        Decline
      </Button>

      <ConfirmDialog
        text={transactionDialogState.confirmationMessage}
        isLoading={isActionPending}
        actionText={`${transactionDialogState.actionType}`}
        open={transactionDialogState.open}
        onClose={onDialogClose}
        action={doAction}
      />
    </div>
  );
}
